package br.com.vvrefeicoes.portal.feature.rmp

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.width
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppBanner
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.RmpEvaluation
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Save
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.rmp_decision_cancel
import portalvv.shared.generated.resources.rmp_evaluation_actions
import portalvv.shared.generated.resources.rmp_evaluation_admission
import portalvv.shared.generated.resources.rmp_evaluation_average
import portalvv.shared.generated.resources.rmp_evaluation_name
import portalvv.shared.generated.resources.rmp_evaluation_opinion
import portalvv.shared.generated.resources.rmp_evaluation_period
import portalvv.shared.generated.resources.rmp_evaluation_role
import portalvv.shared.generated.resources.rmp_evaluation_save
import portalvv.shared.generated.resources.rmp_evaluation_scores_hint
import portalvv.shared.generated.resources.rmp_evaluation_sector
import portalvv.shared.generated.resources.rmp_evaluation_skills
import portalvv.shared.generated.resources.rmp_evaluation_title
import portalvv.shared.generated.resources.rmp_new_date_placeholder

/** Períodos de avaliação para a nova função (mesmas chaves da API). */
private val EVALUATION_PERIODS = listOf("45_dias" to "45 dias", "90_dias" to "90 dias", "sem_periodo" to "Sem período")

/** Campos de texto da avaliação enviados como estão (as notas e a data têm tratamento próprio). */
private val EVALUATION_TEXTS =
    listOf("nome", "cargo", "setor", "periodo_avaliacao", "habilidades_desenvolver", "acoes_executar", "parecer_avaliacao")

/** Média das notas preenchidas, com uma casa (como o "MÉDIA GERAL" do sistema antigo). */
internal fun evaluationAverage(scores: Collection<String>): String {
    val values = scores.mapNotNull { it.toIntOrNull()?.takeIf { score -> score in 0..10 } }
    if (values.isEmpty()) return "-"
    val tenths = (values.sum() * 10 + values.size / 2) / values.size
    return "${tenths / 10},${tenths % 10}"
}

/**
 * Avaliação de desempenho da mudança de cargo (FOR_RH_10.01): identificação, período, as 16 notas de 0 a 10 com a
 * média, comentários e o parecer. Preenchida pelo gestor da unidade; depois dela a mudança de cargo segue para o RH.
 */
@Composable
internal fun RmpEvaluationDialog(
    evaluation: RmpEvaluation,
    number: String,
    controller: RmpApprovalController,
    onDismiss: () -> Unit,
    onSaved: () -> Unit,
) {
    var texts by remember(evaluation) { mutableStateOf(evaluation.data - "data_admissao") }
    var admission by remember(evaluation) { mutableStateOf(isoToDisplayDate(evaluation.data["data_admissao"].orEmpty())) }
    var scores by remember(evaluation) {
        mutableStateOf(evaluation.criteria.associate { it.key to (it.score?.toString() ?: "") })
    }

    fun setText(
        key: String,
        value: String,
    ) {
        texts = texts + (key to value)
    }

    AppModalCard(title = stringResource(Res.string.rmp_evaluation_title, number), onDismiss = onDismiss) {
        controller.errorMessage?.let { message ->
            AppBanner(text = message, onDismiss = controller::dismissError, isError = true)
        }
        AppTextField(
            value = texts["nome"].orEmpty(),
            onValueChange = { setText("nome", it.take(255)) },
            label = stringResource(Res.string.rmp_evaluation_name),
            modifier = Modifier.fillMaxWidth(),
        )
        AppTextField(
            value = admission,
            onValueChange = { admission = maskDate(it) },
            label = stringResource(Res.string.rmp_evaluation_admission),
            placeholder = stringResource(Res.string.rmp_new_date_placeholder),
            modifier = Modifier.fillMaxWidth(),
        )
        AppTextField(
            value = texts["cargo"].orEmpty(),
            onValueChange = { setText("cargo", it.take(255)) },
            label = stringResource(Res.string.rmp_evaluation_role),
            modifier = Modifier.fillMaxWidth(),
        )
        AppTextField(
            value = texts["setor"].orEmpty(),
            onValueChange = { setText("setor", it.take(255)) },
            label = stringResource(Res.string.rmp_evaluation_sector),
            modifier = Modifier.fillMaxWidth(),
        )
        Text(text = stringResource(Res.string.rmp_evaluation_period), style = MaterialTheme.typography.titleSmall)
        Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
            EVALUATION_PERIODS.forEach { (key, label) ->
                Row(
                    modifier = Modifier.clickable { setText("periodo_avaliacao", key) },
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    RadioButton(selected = texts["periodo_avaliacao"] == key, onClick = { setText("periodo_avaliacao", key) })
                    Text(text = label, style = MaterialTheme.typography.bodyMedium)
                }
            }
        }
        Text(
            text = stringResource(Res.string.rmp_evaluation_scores_hint),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        evaluation.criteria.groupBy { it.group }.forEach { (group, criteria) ->
            HorizontalDivider()
            Text(text = group, style = MaterialTheme.typography.titleSmall)
            criteria.forEach { criterion ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
                ) {
                    Text(text = criterion.label, style = MaterialTheme.typography.bodyMedium, modifier = Modifier.weight(1f))
                    AppTextField(
                        value = scores[criterion.key].orEmpty(),
                        onValueChange = { input ->
                            val digits = input.filter(Char::isDigit).take(2)
                            if (digits.isEmpty() || digits.toInt() <= 10) scores = scores + (criterion.key to digits)
                        },
                        isError = scores[criterion.key].isNullOrEmpty(),
                        modifier = Modifier.width(72.dp),
                    )
                }
            }
        }
        HorizontalDivider()
        Text(
            text = stringResource(Res.string.rmp_evaluation_average, evaluationAverage(scores.values)),
            style = MaterialTheme.typography.titleMedium,
            color = MaterialTheme.colorScheme.primary,
        )
        AppTextField(
            value = texts["habilidades_desenvolver"].orEmpty(),
            onValueChange = { setText("habilidades_desenvolver", it.take(5000)) },
            label = stringResource(Res.string.rmp_evaluation_skills),
            singleLine = false,
            modifier = Modifier.fillMaxWidth(),
        )
        AppTextField(
            value = texts["acoes_executar"].orEmpty(),
            onValueChange = { setText("acoes_executar", it.take(5000)) },
            label = stringResource(Res.string.rmp_evaluation_actions),
            singleLine = false,
            modifier = Modifier.fillMaxWidth(),
        )
        AppTextField(
            value = texts["parecer_avaliacao"].orEmpty(),
            onValueChange = { setText("parecer_avaliacao", it.take(5000)) },
            label = stringResource(Res.string.rmp_evaluation_opinion),
            singleLine = false,
            modifier = Modifier.fillMaxWidth(),
        )
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12, Alignment.End),
        ) {
            AppButton(
                text = stringResource(Res.string.rmp_decision_cancel),
                onClick = onDismiss,
                enabled = !controller.deciding,
                variant = PortalButtonVariant.Text,
            )
            AppButton(
                text = stringResource(Res.string.rmp_evaluation_save),
                onClick = {
                    val data =
                        buildJsonObject {
                            EVALUATION_TEXTS.forEach { key -> put(key, JsonPrimitive(texts[key].orEmpty())) }
                            put("data_admissao", JsonPrimitive(displayToIsoDate(admission)))
                            scores.forEach { (key, value) -> put(key, JsonPrimitive(value)) }
                        }
                    controller.saveEvaluation(data, onSaved)
                },
                enabled = !controller.deciding,
                variant = PortalButtonVariant.Filled,
                leadingIcon = Lucide.Save,
            )
        }
    }
}
